import React, { useRef, useState, useEffect, useCallback } from "react";
import { Modal, IconButton, Box } from "@mui/material";
import { PlayCircleFilled, PauseCircleFilled, Close as CloseIcon } from "@mui/icons-material";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { BIBlue } from "../../../assets/helpers";

const PreviewMontageModal = ({
  open,
  onClose,
  selectedMontage,
}) => {
  const audioRef = useRef();
  /* State for audio/global play */
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const videoRefs = useRef({});

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setCurrentIndex(0);
      setIsAudioPlaying(true); // Auto-play on open
    } else {
      setIsAudioPlaying(false);
    }
  }, [open]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => {
      const next = prev + 1;
      const total = selectedMontage?.fileList?.length || 0;
      if (next >= total) {
        // Stop playback at end of list
        setTimeout(() => setIsAudioPlaying(false), 0);
        return 0;
      }
      return next;
    });
  }, [selectedMontage?.fileList?.length]);

  const handleChange = (index) => {
    setCurrentIndex(index);
  };

  // Handle Playback Logic (Video & Image)
  useEffect(() => {
    if (!selectedMontage?.fileList) return;

    const currentItem = selectedMontage.fileList[currentIndex];
    const isVideo = currentItem?.fileType === "video" || currentItem?.fileUrl?.endsWith(".mp4");

    // Manage Video Playback via Refs
    Object.keys(videoRefs.current).forEach((key) => {
      const vid = videoRefs.current[key];
      if (vid) {
        if (parseInt(key) === currentIndex && isAudioPlaying && isVideo) {
          vid.play().catch(() => { });
        } else {
          vid.pause();
          if (parseInt(key) !== currentIndex) {
            vid.currentTime = 0; // Reset others
          }
        }
      }
    });

    if (!isAudioPlaying) return;

    if (!isVideo) {
      // Image: Auto-advance after 5 seconds
      const timer = setTimeout(() => {
        handleNext();
      }, 5000);
      return () => clearTimeout(timer);
    }
    // Video: Handled by onEnded on the element itself
  }, [currentIndex, isAudioPlaying, selectedMontage, handleNext]);

  const musicUrl = selectedMontage?.musicUrl;

  const togglePlay = () => {
    if (audioRef.current.paused) {
      audioRef.current
        .play()
        .catch((error) => console.error("togglePlay-> Audio play error: ", error));
      setIsAudioPlaying(true);
    } else {
      audioRef.current.pause();
      setIsAudioPlaying(false);
    }
  };

  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsAudioPlaying(false);
    }
    onClose();
  };

  useEffect(() => {
    if (audioRef.current && musicUrl) {
      audioRef.current.src = musicUrl;
      if (isAudioPlaying) {
        audioRef.current
          .play()
          .catch((error) => console.error("useEffect-> Audio play error: ", error));
      }
    }
  }, [musicUrl, isAudioPlaying]);

  useEffect(() => {
    const audioElement = audioRef.current;
    const handlePause = () => setIsAudioPlaying(false);
    const handlePlay = () => setIsAudioPlaying(true);

    if (audioElement) {
      audioElement.addEventListener("pause", handlePause);
      audioElement.addEventListener("play", handlePlay);
    }

    return () => {
      if (audioElement) {
        audioElement.removeEventListener("pause", handlePause);
        audioElement.removeEventListener("play", handlePlay);
      }
    };
  }, []);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          maxWidth: 600,
          margin: "auto",
          mt: 5,
          bgcolor: "background.paper",
          p: 1
        }}>
        <Box display="flex">
          <IconButton onClick={handleClose} sx={{ color: BIBlue }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <audio ref={audioRef} />

        <Box position="relative">
          <Carousel
            selectedItem={currentIndex}
            onChange={handleChange}
            autoPlay={false} // Manual control
            infiniteLoop={false} // Disable infinite loop to prevent cloning glitches
            showThumbs={false}
            showArrows={false}
            showStatus={false}
            showIndicators={false}
            stopOnHover={false}
          >
            {selectedMontage?.fileList?.map((image, index) => {
              const isVideo = image.fileType === "video" || image.fileUrl?.endsWith(".mp4");
              // isActive logic logic is now moved to effect

              return (
                <Box
                  key={index}
                  position="relative"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height={500}
                  overflow="hidden"
                >
                  {isVideo ? (
                    <video
                      src={image.fileUrl}
                      controls={false}
                      ref={(el) => (videoRefs.current[index] = el)}
                      onEnded={handleNext}
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  ) : (
                    <img
                      src={image.fileUrl}
                      alt={image.imageDescription || "Slide"}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  )}

                  {image?.imageDescription?.trim() && (
                    <Box
                      position="absolute"
                      bottom={0}
                      left={0}
                      width="100%"
                      py={2}
                      px={3}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      sx={{
                        background: "linear-gradient(to top, rgba(0, 0, 0, 0.6), transparent)",
                      }}
                    >
                      <h2 style={{ color: "#fff", fontSize: "20px", textAlign: "center", margin: 0 }}>
                        {image.imageDescription}
                      </h2>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Carousel>

          <IconButton
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: BIBlue
            }}
            onClick={togglePlay}>
            {isAudioPlaying ? <PauseCircleFilled /> : <PlayCircleFilled />}
          </IconButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default PreviewMontageModal;
